import React, { useState } from "react";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import MailOutlinedIcon from "@mui/icons-material/MailOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  CANCELLATION_POLICY,
  CUSTOMER_CARE_HOURS,
  CUSTOMER_CARE_OPTION,
  CUSTOMER_CARE_PHONE,
  CUSTOMER_CARE_PHONE_HREF,
} from "constant/patientPortalConstants";

interface HelpCardProps {
  onContactUs: () => void;
}

interface HelpRowProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  action: React.ReactNode;
}

// Same row as the Benefits Summary contact card: icon, title and line, action.
const HelpRow = ({ icon, title, children, action }: HelpRowProps) => (
  <Stack
    direction={{ xs: "column", md: "row" }}
    alignItems={{ xs: "flex-start", md: "center" }}
    spacing={2}
  >
    <Avatar sx={{ bgcolor: "primary.light", color: "primary.main" }}>{icon}</Avatar>
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="subtitle1" component="p" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {children}
      </Typography>
    </Box>
    <Box sx={{ flexShrink: 0, width: { xs: "100%", md: "auto" } }}>{action}</Box>
  </Stack>
);

const HelpCard = ({ onContactUs }: HelpCardProps) => {
  const [policyOpen, setPolicyOpen] = useState(false);

  return (
    <Paper component="section" aria-labelledby="help-heading" sx={{ p: 3 }}>
      <Typography variant="h6" component="h2" id="help-heading" sx={{ mb: 2 }}>
        Help
      </Typography>

      <Stack spacing={2} divider={<Divider flexItem />}>
        <HelpRow
          icon={<EventBusyOutlinedIcon />}
          title="Cancellation policy"
          action={
            <Button
              variant="outlined"
              startIcon={<DescriptionOutlinedIcon />}
              onClick={() => setPolicyOpen(true)}
              sx={{ minHeight: 44 }}
            >
              Read the policy
            </Button>
          }
        >
          {CANCELLATION_POLICY.summary}
        </HelpRow>

        <HelpRow
          icon={<SupportAgentOutlinedIcon />}
          title="Customer Care"
          action={
            <Button
              variant="outlined"
              startIcon={<MailOutlinedIcon />}
              onClick={onContactUs}
              sx={{ minHeight: 44 }}
            >
              Send a message
            </Button>
          }
        >
          <Link href={CUSTOMER_CARE_PHONE_HREF} color="inherit">
            {CUSTOMER_CARE_PHONE}
          </Link>
          , {CUSTOMER_CARE_OPTION} · {CUSTOMER_CARE_HOURS}
        </HelpRow>
      </Stack>

      <Dialog
        open={policyOpen}
        onClose={() => setPolicyOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="cancellation-policy-title"
      >
        <DialogTitle id="cancellation-policy-title">Cancellation policy</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>{CANCELLATION_POLICY.intro}</Typography>
            <Box component="ul" sx={{ pl: 2.5, my: 0 }}>
              {CANCELLATION_POLICY.rules.map((rule) => (
                <Typography component="li" key={rule} sx={{ mb: 1 }}>
                  {rule}
                </Typography>
              ))}
            </Box>
            <Typography>{CANCELLATION_POLICY.closing}</Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={() => setPolicyOpen(false)} sx={{ minHeight: 44 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default HelpCard;
